import type { Scene } from '@datum-sdk/engine';
import type { SceneConfig, SkyboxConfig } from '../sceneConfig';
import { preloaderParkPose } from './nativeRouteCinematic';
import { ARAFAT_CAMERA_ROUTES } from './routes/arafatRoutes';
import { KAABA_2000BCE_CAMERA_ROUTES } from './routes/kaaba2000BceRoutes';
import { KAABA_605_CAMERA_ROUTES } from './routes/kaaba605Routes';
import { KAABA_FUTURE_CAMERA_ROUTES } from './routes/kaabaFutureRoutes';
import { KAABA_INSIDE_CAMERA_ROUTES } from './routes/kaabaInsideRoutes';
import { KAABA_OTTOMAN_CAMERA_ROUTES } from './routes/kaabaOttomanRoutes';
import { KAABA_CAMERA_ROUTES } from './routes/kaabaRoutes';
import { MINA_CAMERA_ROUTES } from './routes/minaRoutes';
import { MUZDALIFA_CAMERA_ROUTES } from './routes/muzdalifaRoutes';
import { OVERALL_CAMERA_ROUTES } from './routes/overallRoutes';

/**
 * Hand-authored datum-sdk `Scene` definitions for migrated scenes.
 *
 * This is the deliberate replacement for the `sceneConfigToScene` runtime
 * translator. Rather than convert Pilgrim's `SceneConfig` (Euler rotations,
 * separate preview transform, `pointSizeFactor`, reveal-distance-scale, …) into
 * the SDK's `Scene` on every mount — which is where every orientation / size /
 * "soapy" / reveal-as-fade bug came from — we author the scene DIRECTLY in the
 * SDK's form, exactly like the SDK team's own `editor-demo` `BUILT_IN_SCENES`.
 *
 * Split of responsibilities:
 * - **Read from `SceneConfig` (env/device DATA, no conversion):** splat URL per
 *   quality tier, preview-model URL, skybox URLs/intensity/rotation, background
 *   colour, camera pose, fov, near/far. These vary by deployment/CDN/device and
 *   stay single-sourced in the scene config during the migration.
 * - **Authored natively (SDK FORM, the bits that used to be translated):** the
 *   splat transform as a quaternion, the preview transform, and the
 *   reveal/unreveal recipe. No Euler→quaternion, no relative-preview matrix, no
 *   radius-floor heuristic, no `pointSizeFactor` baseline mismatch.
 */

export interface NativeSceneOpts {
  isMobile: boolean;
  quality: 'low' | 'middle' | 'high';
  /** Touch-capable device — gates orbit pan (two-finger zoom-only on touch). */
  isTouch: boolean;
}

type NativeSceneBuilder = (config: SceneConfig, opts: NativeSceneOpts) => Scene;

// The engine never reads `createdAt`; a fixed epoch keeps the builder pure
// (no `Date.now()`), safe at mount and trivial to snapshot-test.
const EPOCH = '1970-01-01T00:00:00.000Z';

/** Hex `0xRRGGBB` → SDK `RgbaColor` ([r,g,b,a] in 0..1). */
const hexToRgba = (hex: number): [number, number, number, number] => [
  ((hex >> 16) & 0xff) / 255,
  ((hex >> 8) & 0xff) / 255,
  (hex & 0xff) / 255,
  1,
];

/**
 * The skybox URL the SDK path loads: the 2K variant wherever there is one,
 * desktop included. The engine shows the sky on the first revealed frame and
 * uploads it to the GPU right there — mid-flyover — where an 8K equirect blocks
 * the frame for ~350 ms (measured, desktop Chrome) against ~20 ms for 2K. Legacy
 * pre-uploads under the preloader; the engine offers no way to (CR E1).
 */
const pickSkyboxUrl = (sky: SkyboxConfig | undefined): string | undefined =>
  sky && (sky.mobileUrl ?? sky.url);

/** Resolve the splat file URL for the current device tier (env/CDN-aware). */
const pickSplatUrl = (config: SceneConfig, opts: NativeSceneOpts): string => {
  const sel = opts.isMobile
    ? config.splat.defaultByQuality.mobile
    : config.splat.defaultByQuality.desktop;
  const key = typeof sel === 'string' ? sel : sel[opts.quality];
  const variant = config.splat.variants.find((v) => v.key === key) ?? config.splat.variants[0];
  return variant.url;
};

/**
 * Identity wrapper that exists purely to TYPECHECK the scene literals below.
 *
 * A literal RETURNED from a contextually-typed builder is not
 * excess-property-checked by TypeScript, so an SDK field that is renamed or
 * removed upstream keeps compiling here and silently stops doing anything —
 * exactly how `orbit.pivotReturn` went dead unnoticed across six scenes. In
 * ARGUMENT position the literal is fresh, so an unknown key is an error.
 */
const defineScene = (scene: Scene): Scene => scene;

/**
 * Legacy-parity aim return — `cameraController.home` with a gesture trigger.
 *
 * After a POI fly displaces the orbit pivot, the viewer's next orbit gesture
 * starts an eased return of the LOOK-AT to the scene's home target while the
 * gesture keeps steering: legacy `SceneControls._orbitTargetOverride` glide
 * (600 ms, easeInOut). This replaces the SDK's earlier `orbit.pivotReturn`,
 * which no longer exists — the home-view action subsumes it.
 *
 * - `scope: 'look-at'` — only the aim travels; position / roll / fov / mode are
 *   untouched. It is also the ONLY form that can move under a live gesture, and
 *   it is orbit-only, which is why the two forced-FPS scenes (mina,
 *   kaaba-inside) don't carry this block: there is no orbit pivot to return.
 * - Home target defaults to the scene camera's own `lookAt` (= legacy
 *   `controls.orbitCenter`). `overall` overrides it — its authored `lookAt` is
 *   the PREVIEW center, not the main one.
 * - `disablePoiDeselect` keeps the POI selected (and its marker highlighted)
 *   through the return, as legacy did. Without it the engine clears the
 *   selection first, so the marker would drop its highlight on the first drag.
 * - `control` / `hotkey` stay off: Pilgrim owns its own UI, and no engine-drawn
 *   button or shortcut should appear.
 */
const HOME_AIM_RETURN = {
  scope: 'look-at',
  gesture: 'next-gesture',
  duration: 600,
  easing: 'easeInOut',
  disablePoiDeselect: true,
} as const;

const buildKaabaScene: NativeSceneBuilder = (config, opts) => {
  // Camera: the preloader cinematic's STARTING frame — legacy initialPosition
  // with legacy's `clampMinHeight` baked into y (the legacy controls clamped
  // (0,0,1) → (0,0.08,1) on the first frame; the SDK has no such clamp). The
  // routes (`routes/kaabaRoutes.ts`, opt-in below) then fly enter → turntable
  // → exit → flyover, resting on the flyover's last view; skipAnimations lands
  // there directly (createSdkScene). lookAt = orbitCenter — ALSO the `home`
  // aim-return target, keep them equal.
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };
  // Reveal origin = where the exit PARKS the camera when the reveal starts.
  // Explicit — `origin: 'camera'` resolves from the live camera, which the
  // flyover moves during the reveal.
  const revealOrigin = preloaderParkPose(KAABA_CAMERA_ROUTES)?.position ?? [
    camPos.x,
    camPos.y,
    camPos.z,
  ];

  // Skybox: the 2K equirect on every device (pickSkyboxUrl), also as `url2k`.
  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // 'spread' radius is WORLD-space (the SDK divides by the splat's world scale
  // to reach local units; origin = camera) — the front's total travel distance,
  // NOT the visible wave width. With the parametrized wave (bandWidth et al)
  // splats beyond the radius no longer hard-hide — they fade in together with
  // the outermost shell — so an under-sized radius merely compresses the tail
  // instead of popping. 2.4 covers this model with margin at our scale 0.02.
  const REVEAL_RADIUS = 2.4;

  // Legacy's nominal `revealAnimationDuration` was 3 s, but its quartic front
  // (`0.4·t⁴ + 0.5`) overshot the model — full cover landed at ~2.3 s. The SDK
  // front reaches `radius` exactly at `duration`, so 2.5 s matches the legacy
  // pacing. Tune by eye here, never via the radius.
  const REVEAL_DURATION = 2.5;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // Marker clustering: kaaba's sights sit tightly around the cube, so backing
    // off piles their anchors on top of each other. `createSdkScene` spreads
    // this object when it adds the `pois`, so the flag survives. Label
    // declutter stays OFF (the SDK default) — labels that survive clustering may
    // still overlap; that is accepted here over having them suppressed.
    // The approach flight rides `clustering.easing`'s own default (`'easeOut'`) —
    // the SAME curve `flyTo` passes for a POI flight (POI_FLIGHT_EASING in
    // createSdkScene), so a cluster click and a marker click feel alike.
    // `speed` is that flight's RATE in world units/sec, and it has to be authored
    // here: the SDK default (12) is tuned for a metric scene, while this world is
    // scale 0.02 (~2 units across), so every approach travels a fraction of a
    // unit and lands on the engine's 250 ms floor — the "too fast / jerky" read.
    // 0.6 mirrors `POI_FLIGHT_SPEED_ORBIT` in createSdkScene, so both flights
    // derive their duration from the same rate; the engine then bounds it to its
    // own non-authorable 250–800 ms comfort envelope (ours is 0.5–1.5 s, so a
    // long approach caps at 800 ms — close enough by eye).
    // ponytail: calibration knob — nudge `speed` DOWN to slow the approach, UP to
    // quicken it. Other clustering fields stay on defaults (radiusPx 48, world
    // gate derived from POI spacing); tune those if the grouping reads wrong.
    annotations: {
      clustering: { enabled: true, speed: 0.6 },
    },
    models: [
      {
        id: 'kaaba-splat',
        file: {
          id: 'kaaba-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'kaaba',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the four routes the choreography
      // plays by id. Replace this array with one the backend serves and the
      // flights change without touching code (`routes/kaabaRoutes.ts`).
      cameraRoutes: KAABA_CAMERA_ROUTES,
      // All kaaba splat variants share this transform: position (-0.008, 0,
      // 0.008), Euler z=π → quaternion [0, 0, 1, 0], uniform scale 0.02.
      transform: { position: [-0.008, 0, 0.008], quaternion: [0, 0, 1, 0], scale: 0.02 },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        // Orbit zoom range from Pilgrim's config. The SDK default `minDistance`
        // is 0.5 — on our scale-0.02 world (camera ~0.57 from target) that blocks
        // close zoom almost immediately. Pilgrim's kaaba uses 0 (zoom all the way
        // in); the obstacle collider then stops the camera at the kaaba surface.
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // Two-finger touch = zoom only. `disablePan` (SDK C3) drops the pan
          // sub-step of the default `touches.two: 'dolly-pan'` gesture, so on
          // touch two fingers dolly-only while one finger still rotates; desktop
          // keeps right-drag pan. `isTouch` is host policy (see createSdkScene).
          disablePan: opts.isTouch,
        },
        // Legacy-parity aim return; home target = the camera `lookAt` above.
        home: HOME_AIM_RETURN,
      },
      // Camera collision, authored directly in SDK terms (NOT translated from
      // SceneConfig.colliders — same native-form principle as transform/reveal).
      // SDK collider scale semantics: box = FULL size; cylinder = [diameter,
      // height, diameter]; sphere = diameter. World-space. role 'obstacle' =
      // camera stays outside, 'containment' = camera stays inside. cameraRadius
      // 0.05 = Pilgrim's kaaba value.
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            id: 'kaaba-cube',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [-0.31904604734307435, 0.1668963598394823, 0.46813923921190986],
              quaternion: [0, 0, 0, 1],
              scale: [0.26, 0.31, 0.29],
            },
          },
          {
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            // radius 1.15 → diameter 2.3. Height 0.5 (Y 0.0–0.5, center 0.25):
            // this cylinder is now the SOLE camera Y limiter (the separate hard
            // position-bounds Y-clamp was dropped — see createSdkScene), so its
            // Y extent sets how low (floor, no fall-through) and how high
            // (ceiling) the orbit camera can go, minus the camera-collision
            // inset. ponytail: calibration knob — nudge scale.y / position.y if
            // the floor still clips or the ceiling sits too high/low.
            transform: {
              position: [-0.3, 0.25, 0.5],
              quaternion: [0, 0, 0, 1],
              scale: [2.3, 0.5, 2.3],
            },
          },
        ],
      },
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        // Manual reveal: the SDK loads + shows the preview, then STAYS in 'preview'
        // until the host calls `engine.setSceneState('done')`. Pilgrim triggers that
        // only after its own progress bar reaches 100% and fades out (createSdkScene),
        // so the reveal never plays under a still-visible loading bar. Default 'auto'
        // starts the reveal the instant the splat finishes downloading.
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // Z-flip so the preview aligns with the z=π splat. combineTransforms
        // (scene z=π ∘ preview z=π) = identity rotation × scale 0.02 — exactly
        // Pilgrim's PREVIEW_MODEL_TRANSFORM. Same value the SDK's kaaba demo uses.
        previewTransform: { quaternion: [0, 0, 1, 0] },
        // No previewSizeMultiplier / previewAnimation overrides on purpose: the
        // SDK keeps the GLB's native point size and DEFAULT_WAVES, which already
        // look right (proven by the SDK's own kaaba demo). Pilgrim's
        // `pointSizeFactor: 3.0` compensates for ITS shader's 1px baseline and
        // does NOT port — feeding it here triples the size → "soapy" particles.
        // Legacy-parity recipe — a 1:1 port of Pilgrim's reveal dyno
        // (`visibility = clamp(front − dist·k, 0, 1)`, alpha-only, kaaba
        // values: scale 0.02, revealDistanceScale 5 → k = 0.1, front =
        // 0.4·t⁴ + 0.5):
        //   bandWidth   1/k = 10 local units → 0.2 world m (wide soft band);
        //   startRadius 0.5/k = 5 local → 0.1 world m — the origin sits
        //               mid-band at t=0 (≈50% alpha core), like legacy;
        //   alphaOnly   splats keep full size and FADE in (condensing cloud),
        //               not grow from points;
        //   frontEasing legacy's accelerating quartic ≈ 'easeIn' (quadratic —
        //               the closest available profile);
        //   easing      'linear' — legacy had no per-splat shaping beyond the
        //               linear clamp (the SDK band is already a smoothstep).
        // distanceMetric omitted → 'cylindrical' (XZ), the legacy default for kaaba.
        reveal: {
          type: 'spread',
          origin: revealOrigin,
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          bandWidth: 0.2,
          startRadius: 0.1,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildArafatScene: NativeSceneBuilder = (config, opts) => {
  // Camera: the preloader cinematic's STARTING frame (legacy initialPosition;
  // the Math.max mirrors kaaba's clampMinHeight bake — a no-op here, 0.348 >
  // −1.7). The routes then fly enter → turntable → previewTourEnd → the
  // arafat flyover (1 s approach + 360° ring of views + 1 s descent,
  // `routes/arafatRoutes.ts`), resting on its final view (−0.801, 0.012, 4.146);
  // skipAnimations lands there directly. `previewTourEnd.target` =
  // `controls.orbitCenter` = `splat.revealOrigin` = the summit of Jabal
  // al-Rahma — one semantic center for framing, pivot return AND the reveal
  // origin (= the lookAt here).
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // Arafat's splat world scale is 1 (kaaba's is 0.02) — every distance below
  // is in plain world meters; nothing transfers from the kaaba numbers.
  //
  // Front travel distance: the farthest in-bounds camera point (containment
  // cylinder radius 6.2) is ~6.3 m of spherical distance from the summit
  // origin; 7 covers it with margin. The parametrized wave fades the beyond-
  // radius shell together with the outermost band, so oversizing is safe.
  const REVEAL_RADIUS = 7;

  // Legacy nominal `revealAnimationDuration` was 3.5 s, but its quartic front
  // covered the model at ~77% of nominal (kaaba precedent: 3.0 → 2.5). The
  // SDK front reaches `radius` exactly at `duration` — start at 2.7 and tune
  // by eye, never via the radius.
  const REVEAL_DURATION = 2.7;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // POI markers are never dimmed by colliders here: the sights sit ON the
    // mountain — the surface of the huge sunk obstacle sphere — so the
    // camera→POI ray grazes the collider from most angles and occlusion
    // would keep the markers permanently dimmed. (`createSdkScene` spreads
    // this object when it adds the `pois`, so the flag survives.)
    annotations: { disableOcclusion: true },
    models: [
      {
        id: 'arafat-splat',
        file: {
          id: 'arafat-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'arafat',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour and the flyover the
      // choreography plays by id.
      cameraRoutes: ARAFAT_CAMERA_ROUTES,
      // All arafat variants share `defaultTransform`: Euler {x:π, y:π} (XYZ)
      // ≡ Rz(π) → quaternion [0, 0, 1, 0] — the same z-flip as kaaba, at
      // identity position and scale 1.
      transform: { quaternion: [0, 0, 1, 0] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // Same touch policy as kaaba: two fingers zoom-only (SDK C3).
          disablePan: opts.isTouch,
        },
        // Aim return to the summit after a POI fly — home = camera lookAt above
        // = legacy `controls.orbitCenter`.
        home: HOME_AIM_RETURN,
        // NOTE: legacy `orbitMinPolarAngle: 70°` (no overhead views of the
        // mountain) has no scene-format field (SDK exposes only
        // maxPolarAngle) — createSdkScene arms it via `setCameraConstraints`
        // from `config.controls.orbitMinPolarAngle`. CR C7 tracks the
        // native passthrough.
      },
      // Camera collision in SDK terms (native-form: hand-converted from
      // arafatColliders.ts, not translated at runtime). Same scale semantics
      // as kaaba: box = FULL size; cylinder = [diameter, height, diameter];
      // sphere = diameter.
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            // Jabal al-Rahma itself — a deep-sunk sphere whose top cap is the
            // mountain surface (legacy solid sphere r=3.4 → diameter 6.8).
            id: 'mount-rahma',
            shape: 'sphere',
            role: 'obstacle',
            transform: {
              position: [-0.01, -3.76, -0.02],
              quaternion: [0, 0, 0, 1],
              scale: [6.8, 6.8, 6.8],
            },
          },
          {
            // The summit rock cluster (legacy solid AABB, full size).
            id: 'summit-rock',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [0.004, -0.33, 0.014],
              quaternion: [0, 0, 0, 1],
              scale: [0.17, 0.47, 0.15],
            },
          },
          {
            // Sole camera cage (kaaba pattern: the containment cylinder owns
            // X/Z AND Y). Legacy cylinder spanned Y −2.35…0.35 but a separate
            // `clampMinHeight: −1.7` floor governed — the cylinder floor here
            // is raised to −1.7 for exact legacy parity (`clampMaxHeight:
            // Infinity` was dead: the cylinder top already capped at 0.35).
            // ponytail: calibration knob — nudge position.y / scale.y if the
            // floor or ceiling reads wrong on device.
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            transform: {
              position: [-0.01, -0.675, -0.02],
              quaternion: [0, 0, 0, 1],
              scale: [12.4, 2.05, 12.4],
            },
          },
        ],
      },
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        // Same manual-reveal contract as kaaba: host triggers the reveal after
        // its progress bar fades (createSdkScene).
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // Scene z-flip ∘ preview z-flip = identity — same recipe as kaaba.
        previewTransform: { quaternion: [0, 0, 1, 0] },
        // Legacy-parity recipe — a 1:1 port of the legacy reveal dyno with
        // arafat's values (`revealDistanceScale: 1.5` at world scale 1 →
        // k = 1.5; `revealMode: '3d'`; fixed `revealOrigin` at the summit):
        //   origin      the summit POINT — legacy used a fixed origin here,
        //               not the camera;
        //   spherical   legacy '3d' — full 3D distance (the mountain is tall);
        //   bandWidth   1/k ≈ 0.667 m (wide soft band);
        //   startRadius 0.5/k ≈ 0.333 m — the origin sits mid-band at t=0;
        //   alphaOnly   splats FADE in at full size (condensing cloud);
        //   frontEasing legacy's accelerating quartic ≈ 'easeIn';
        //   easing      'linear' — legacy had no per-splat shaping.
        reveal: {
          type: 'spread',
          origin: [-0.01, -0.45, -0.02],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 0.667,
          startRadius: 0.333,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildMuzdalifaScene: NativeSceneBuilder = (config, opts) => {
  // Camera: the preloader cinematic's STARTING frame (legacy initialPosition;
  // the Math.max mirrors kaaba's clampMinHeight bake — a no-op here, 1.15 >
  // 0.7). The routes then fly enter → turntable → previewTourEnd → the
  // 2-view flyover (`routes/muzdalifaRoutes.ts`), resting on its final view
  // (4.96, 1.23, −0.6); skipAnimations lands there directly. Same
  // one-semantic-center invariant as arafat: `previewTourEnd.target` =
  // `controls.orbitCenter` = `splat.revealOrigin` = the camp center — which
  // is also the lookAt here and the `home` aim-return target.
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  // NIGHT scene: no skybox at all — solid `0x1c1c1c` background; the splat
  // carries baked night colors (thousands of warm tent lights). A daytime sky
  // would bleed through low-alpha splat regions (see muzdalifa.ts).
  //
  // World scale is 1 (world-scale export, identity transform — no z-flip
  // unlike kaaba/arafat, no scale). All distances below are plain meters.
  //
  // Front travel distance: max in-cylinder XZ distance from the reveal origin
  // ≈ 7.36 m (containment r=7, origin ~0.36 m off-axis); the valley splats
  // extend past the CAMERA boundary, but the parametrized wave fades the
  // beyond-radius shell with the outermost band — under/overshoot are both
  // graceful. 8 covers the practical envelope.
  const REVEAL_RADIUS = 8;

  // Legacy nominal `revealAnimationDuration` was 3.5 s; quartic-front
  // correction ≈ ×0.77 (kaaba/arafat precedent) → start at 2.7, tune by eye.
  const REVEAL_DURATION = 2.7;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // POI markers are never dimmed by colliders: the containment cylinder's
    // floor is raised to the legacy camera floor (0.7), which puts the
    // ground-level sights BELOW the cylinder — the camera→marker ray then
    // crosses its bottom cap and occlusion would dim them. (Legacy marked
    // this collider `blocksLineOfSight: false` for the same reason.)
    annotations: { disableOcclusion: true },
    models: [
      {
        id: 'muzdalifa-splat',
        file: {
          id: 'muzdalifa-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'muzdalifa',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour and the flyover the
      // choreography plays by id.
      cameraRoutes: MUZDALIFA_CAMERA_ROUTES,
      // EXPLICIT identity — omitting the transform is NOT identity in the
      // SDK: `buildObjectTransform` defaults the quaternion to [1, 0, 0, 0]
      // (180° around X, "OpenCV → OpenGL re-orientation") and the Y-up
      // world-scale export came out upside down. previewTransform stays
      // omitted — the appearance module's own default IS identity, so the
      // combined preview transform is identity ∘ identity.
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // Same touch policy as kaaba/arafat: two fingers zoom-only (SDK C3).
          disablePan: opts.isTouch,
        },
        // Aim return to the camp center after a POI fly.
        home: HOME_AIM_RETURN,
        // Legacy `orbitMinPolarAngle: 70°` (same as arafat) rides the generic
        // `setCameraConstraints` arming in createSdkScene — no per-scene code.
        // With the preloader cinematic enabled, the arm happens at
        // cinematicDone (NOT loadScene): setCameraState clamps by active
        // constraints, and the tour-exit chord legitimately dips to ~polar 28°
        // (legacy flies it under suspendOrbitLocks).
      },
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            // Sole camera cage. Legacy cylinder spanned Y −9.10…9.36 but a
            // separate `clampMinHeight: 0.7` floor governed (a ~9.8 m gap!) —
            // the cylinder floor here is raised to 0.7 for legacy parity; the
            // 9.36 ceiling is the legacy cylinder top (practically out of
            // reach). ponytail: calibration knob — nudge position.y / scale.y
            // if the floor or ceiling reads wrong on device.
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            transform: {
              position: [-1.6097442003738163, 5.029551540420308, 0.2794973600226701],
              quaternion: [0, 0, 0, 1],
              scale: [14, 8.6591030808406, 14],
            },
          },
        ],
      },
      environment: {
        // Solid night background; no skybox (see the NIGHT note above).
        background: hexToRgba(config.renderer.clearColor),
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // No previewColor / previewSizeMultiplier / previewAnimation: legacy
        // deliberately reverted its night colorMultiplier boost to 1.0 (the
        // boost clamped baked highlights and killed the lit-tents look), so
        // the GLB's own colors are already right.
        // Legacy-parity recipe (`revealDistanceScale: 1.5` at world scale 1 —
        // the same derived numbers as arafat), origin = fixed camp center,
        // spherical metric (legacy `revealMode: '3d'`):
        reveal: {
          type: 'spread',
          origin: [-1.27, 0.17, 0.39],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 0.667,
          startRadius: 0.333,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildMinaScene: NativeSceneBuilder = (config, opts) => {
  // FIRST-PERSON scene — a "standing pilgrim": position frozen (look-only),
  // vertical look clamped to legacy's 30°–150° cone (armed at runtime via
  // `setCameraConstraints` in createSdkScene — no scene-format field, CR C8),
  // POI clicks walk up to the sight from wherever the viewer stands (dynamic
  // pose in createSdkScene's FPS fly path — inexpressible as a static
  // `Poi.camera`).
  //
  // Camera: the preloader cinematic's STARTING frame (legacy initialPosition;
  // Math.max mirrors kaaba's clampMinHeight bake — a no-op here, −1.35 > −2).
  // The routes then fly enter (1.5 s, per-scene previewTourEnterDuration) →
  // turntable → previewTourEnd → the 3-view flyover (`routes/minaRoutes.ts`,
  // whose middle view looks at a DIFFERENT target — the aim rides its own
  // curve), resting on its last view (2.577, 0.632, 2.12); skipAnimations
  // lands there directly. The eye level
  // (y = 0.632) then stays frozen for the whole session — walk-up flights
  // preserve it by construction.
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // World scale 1 (identity export). Reveal derivations for legacy
  // `revealDistanceScale: 2.0` → k = 2: bandWidth 0.5 m, startRadius 0.25 m.
  // Front travel: reveal origin sits ~3.6 m off the containment cylinder's
  // axis → max in-bounds XZ distance ≈ 8.6; 9 covers it with margin.
  const REVEAL_RADIUS = 9;

  // Legacy nominal 2.5 s × the ~0.77 quartic-front correction (kaaba/arafat
  // precedent) ≈ 1.9. Tune by eye, never via the radius.
  const REVEAL_DURATION = 1.9;

  // Legacy look speeds are degrees-per-viewport-drag with desktop/mobile
  // splits (120/80 desktop, 60/40 mobile); the SDK's `pointerRotateSpeed` is
  // rad-per-PIXEL, uniform across axes. These per-device values approximate
  // the legacy feel at typical viewport widths — tune at the FPS-feel gate.
  const pointerRotateSpeed = opts.isMobile ? 0.0027 : 0.002;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // All 3 legacy colliders are `blocksLineOfSight: false` — markers never dim.
    annotations: { disableOcclusion: true },
    models: [
      {
        id: 'mina-splat',
        file: {
          id: 'mina-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'mina',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour and the flyover the
      // choreography plays by id.
      cameraRoutes: MINA_CAMERA_ROUTES,
      // EXPLICIT identity (world-scale Y-up export) — omitting the transform
      // is NOT identity in the SDK (default = 180° X flip; see L7).
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'fps',
      cameraClickNavEnabled: false,
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        // 'fps' ONLY: the default availableModes includes 'fps-gravity', and
        // with our colliders registered the KeyC mode cycle would let the
        // user walk into a gravity fall. Narrowing to one mode also disables
        // the cycle itself.
        availableModes: ['fps'],
        // Look-only recipe (legacy `lockMovement: true`): every movement
        // input zeroed; pointer look stays. Explicit zeros win over the
        // `base.speed` derivations. Residual gap: Alt+WASD / gamepad look at
        // Spark's fixed 2 rad/s is not disableable (CR C10 — accepted).
        fps: {
          moveSpeed: 0,
          scrollSpeed: 0,
          pinchSpeed: 0,
          slideSpeed: 0,
          tapHoldMoveSpeed: 0,
          pointerRotateSpeed,
        },
        clickNavigation: { disabled: true },
        // No `home` block: the aim return is `scope: 'look-at'`, which is
        // orbit-only — and FPS has no orbit pivot to return.
      },
      // Same scale semantics as the other scenes: box = FULL size; cylinder =
      // [diameter, height, diameter]. The two solid boxes are OBBs (33°/34°
      // around Y) — quaternion [0, sin(θ/2), 0, cos(θ/2)].
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            // Sole camera cage. Unlike arafat/muzdalifah the floor is NOT
            // raised to legacy `clampMinHeight` (−2): legacy-FPS never applies
            // the Y-clamp (it lives inside the disabled movement block) and
            // the position is frozen anyway — the cylinder matters only as a
            // flight corridor.
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            transform: {
              position: [-0.7270479985411361, -2.4564076612153865, -1.0680078789918892],
              quaternion: [0, 0, 0, 1],
              scale: [10, 28.990000000000006, 10],
            },
          },
          {
            // Large tent-block building — legacy OBB, 33° around Y.
            id: 'building',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [-5.22986753752741, -1.8729031327206709, -4.05695643109756],
              quaternion: [0, 0.2840153447, 0, 0.9588197349],
              scale: [36, 19, 4],
            },
          },
          {
            // Hill slope — legacy OBB, 34° around Y.
            id: 'mina-hill-part',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [-0.07068487092096953, -4.541185153256894, 3.404884699672243],
              quaternion: [0, 0.2923717047, 0, 0.956304756],
              scale: [15.5, 6.5, 7],
            },
          },
        ],
      },
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // Scene is identity → no previewTransform (appearance default IS
        // identity, unlike the scene transform — see L7).
        // Legacy overrides its preview appearance ramp to 1.5 s (default 3 in
        // legacy, 1 in the SDK — pass the legacy value).
        previewAppearance: { duration: 1.5 },
        // Legacy-parity reveal (`revealDistanceScale: 2.0`, `revealMode: '3d'`,
        // fixed origin at the valley center = orbitCenter = wavesCenter):
        reveal: {
          type: 'spread',
          origin: [-2.43, -1.54, -4.25],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 0.5,
          startRadius: 0.25,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildOverallScene: NativeSceneBuilder = (config, opts) => {
  // ROOT top-down map of Mecca. Free-orbit, NO chat (OverallSceneOverlay
  // curtain instead), 4 synthetic nav-POI beacons (synthesized in
  // createSdkScene from config.navigationPois — a Scene builder stays a pure
  // config→Scene mapper and has no i18n access), and the navigation cinematic
  // (adapter navigateToScene).
  //
  // Camera: the preloader cinematic's STARTING frame (legacy initialPosition;
  // clampMinHeight is −Infinity → Math.max is a no-op). SPLIT ORBIT CENTER:
  // the preview cloud declares its own orbitCenter (1.27, 0, −0.18) — initial
  // frame + tour aim/revolve there; the exit re-aims at previewTourEnd.target
  // = the MAIN orbitCenter (0.18, 0.03, −0.31), where the route leaves the
  // orbit pivot. TOUR-ONLY cinematic (no flyover): rest = previewTourEnd
  // (5.82, 2.08, −0.47).
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.previewModel.orbitCenter ??
    config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };
  const mainOrbitCenter = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // World scale 1 (identity export — NO z-flip, unlike kaaba/arafat). Reveal
  // derivations for legacy `revealDistanceScale: 2.5` → k = 2.5: bandWidth
  // 0.4 m, startRadius 0.2 m. Origin = fixed city point (2.46, 0, −0.36);
  // farthest nav-POI (arafat) sits ≈ 5.1 m out and the splat extends past the
  // POIs → 7 with margin (beyond-radius shells fade gracefully, R3).
  const REVEAL_RADIUS = 7;

  // Legacy nominal 3 s × the ~0.77 quartic-front correction ≈ 2.3.
  const REVEAL_DURATION = 2.3;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    models: [
      {
        id: 'overall-splat',
        file: {
          id: 'overall-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'overall',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour the choreography plays
      // by id; no flyover (`routes/overallRoutes.ts`).
      cameraRoutes: OVERALL_CAMERA_ROUTES,
      // EXPLICIT identity (world-scale export) — omitting the transform is
      // NOT identity in the SDK (default = 180° X flip; see L7).
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // Declarative polar cap (era pattern): camera can't drop below ~5°
          // over the horizon. The 60° MIN polar rides the runtime
          // pitchConstraint in createSdkScene (armed at cinematicDone).
          maxPolarAngle: config.controls?.orbitMaxPolarAngle ?? Math.PI,
          disablePan: opts.isTouch,
        },
        // The home target must be EXPLICIT here: the default (the scene camera
        // `lookAt` above) is the PREVIEW center — wrong home. First orbit scene
        // with a split center. `scope: 'look-at'` uses only this `lookAt`.
        home: {
          ...HOME_AIM_RETURN,
          camera: { lookAt: [mainOrbitCenter.x, mainOrbitCenter.y, mainOrbitCenter.z] },
        },
      },
      // NO collisions: legacy has `colliders: []` and every Y-clamp disabled
      // (±Infinity) — the free orbit is bounded only by min/maxDistance and
      // the polar limits.
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // Scene is identity → no previewTransform (appearance default IS
        // identity, unlike the scene transform — see L7).
        previewAppearance: { duration: 1.5 },
        // Legacy-parity reveal (`revealDistanceScale: 2.5`, `revealMode: '3d'`,
        // fixed origin at the city center-east):
        reveal: {
          type: 'spread',
          origin: [2.46, 0, -0.36],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 0.4,
          startRadius: 0.2,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildKaabaInsideScene: NativeSceneBuilder = (config, opts) => {
  // Inside the Kaaba — FIRST-PERSON room, stack child of kaaba-sdk. Same
  // "standing pilgrim" recipe as mina (look-only FPS, vertical cone armed at
  // runtime via setCameraConstraints — kaabaInside.ts authors 30°–150°), but
  // the camera cage is a HOLLOW BOX: `shape: 'box', role: 'containment'` —
  // the SDK's per-axis clamp in the collider's local frame is an exact port
  // of legacy resolveBoxHollowCollision.
  //
  // Camera: the preloader cinematic's STARTING frame (legacy initialPosition;
  // Math.max mirrors the clampMinHeight bake — a no-op here, 3.77 > 0).
  // SPLIT ORBIT CENTER: this scene's preview model declares its OWN
  // orbitCenter (the point-cloud's center, elevated mid-room) — the initial
  // frame AND the whole tour aim/revolve there so the cloud stays centered
  // on screen (legacy PreviewTour contract; the converter reads the same field).
  // The exit then re-aims at previewTourEnd.target = the main orbitCenter.
  // TOUR-ONLY cinematic (no flyover in the config): enter (1.5 s per-scene
  // previewTourEnterDuration) → turntable → exit parks at `previewTourEnd`
  // (−1.86, 0.25, −3.99) = the rest pose; skipAnimations lands there via
  // the exit's landing (`preloaderRestPose`). Parts of the tour fly
  // OUTSIDE the containment box (tourStart x −6.05 vs wall −3.99) — the
  // preloader lifts collisions for its routes (`routes/kaabaInsideRoutes.ts`).
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.previewModel.orbitCenter ??
    config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  // World scale 1 (identity export). Reveal derivations for legacy
  // `revealDistanceScale: 2.0` → k = 2: bandWidth 0.5 m, startRadius 0.25 m
  // (same as mina). Origin = the room center (orbitCenter = revealOrigin);
  // the visible walls sit ~2–4 m out — 5 covers them with margin (the
  // parametrized wave fades any beyond-radius shell gracefully, R3).
  const REVEAL_RADIUS = 5;

  // Legacy nominal 3 s × the ~0.77 quartic-front correction ≈ 2.3.
  const REVEAL_DURATION = 2.3;

  // Same per-device look-speed approximation as mina — tune at the FPS gate.
  const pointerRotateSpeed = opts.isMobile ? 0.0027 : 0.002;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // Legacy collider is `blocksLineOfSight: false` — markers never dim.
    annotations: { disableOcclusion: true },
    models: [
      {
        id: 'kaaba-inside-splat',
        file: {
          id: 'kaaba-inside-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'kaaba-inside',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour the choreography plays
      // by id; no flyover.
      cameraRoutes: KAABA_INSIDE_CAMERA_ROUTES,
      // EXPLICIT identity (world-scale Y-up export) — omitting the transform
      // is NOT identity in the SDK (default = 180° X flip; see L7).
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'fps',
      cameraClickNavEnabled: false,
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        // 'fps' ONLY — mandatory here: with the containment collider
        // registered, the default availableModes keeps 'fps-gravity' and the
        // KeyC cycle would drop the camera into a gravity fall inside a
        // sealed room. Narrowing to one mode also disables the cycle.
        availableModes: ['fps'],
        // Look-only recipe (legacy `lockMovement: true`) — see buildMinaScene.
        fps: {
          moveSpeed: 0,
          scrollSpeed: 0,
          pinchSpeed: 0,
          slideSpeed: 0,
          tapHoldMoveSpeed: 0,
          pointerRotateSpeed,
        },
        clickNavigation: { disabled: true },
        // No `home` block: the aim return is orbit-only (see mina).
      },
      collisions: {
        enabled: true,
        // Legacy camera radius. The SDK default is 0.15 — 3× larger, the
        // walls would visibly "move in".
        camera: { radius: 0.05 },
        colliders: [
          {
            // The room. Legacy hollow box, authored by eye (its source
            // comment mis-cites log-encoded .sog meta as meters — the
            // authored size is the tuned truth; ponytail: calibration knob).
            // scale = FULL size, per-axis containment clamp in local frame.
            id: 'kaaba-inside-room',
            shape: 'box',
            role: 'containment',
            transform: {
              position: [-0.14, 0.585, -0.402],
              quaternion: [0, 0, 0, 1],
              scale: [7.7, 10.5, 9],
            },
          },
        ],
      },
      environment: {
        // Windowless room — solid dark background, no skybox key at all
        // (muzdalifah pattern).
        background: hexToRgba(config.renderer.clearColor),
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // Scene is identity → no previewTransform (appearance default IS
        // identity, unlike the scene transform — see L7).
        // Legacy overrides its preview appearance ramp to 1.5 s.
        previewAppearance: { duration: 1.5 },
        // Legacy-parity reveal (`revealDistanceScale: 2.0`, `revealMode: '3d'`,
        // fixed origin at the room center = orbitCenter):
        reveal: {
          type: 'spread',
          origin: [0.14, 0.88, -1.45],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 0.5,
          startRadius: 0.25,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildKaaba2000BceScene: NativeSceneBuilder = (config, opts) => {
  // Bronze Age Kaaba era. One-semantic-center invariant is exact by
  // construction (previewTourEnd.target = orbitCenter = revealOrigin =
  // wavesCenter — literally the same object in the config).
  // Camera: the preloader cinematic's STARTING frame (legacy initialPosition;
  // clampMinHeight is −Infinity → Math.max is a no-op). TOUR-ONLY cinematic —
  // no flyover in the era config, so the exit parks at `previewTourEnd` and
  // that IS the rest pose (skipAnimations lands there via the exit's landing,
  // `preloaderRestPose`).
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // World scale 1 (identity export). Reveal derivations for legacy
  // `revealDistanceScale: 0.8` → k = 0.8 — the WIDEST soft band of any scene
  // (1.25 m; startRadius 0.625 m). Max in-cage spherical distance from the
  // origin ≈ 9.7 (containment r=9, origin ~0.02 off-axis) → 10 with margin.
  const REVEAL_RADIUS = 10;

  // Legacy nominal 3 s × the ~0.77 quartic-front correction ≈ 2.3.
  const REVEAL_DURATION = 2.3;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // NO disableOcclusion: the era cube collider deliberately occludes
    // markers behind it in legacy (kaaba pattern — unlike arafat/muzdalifah/
    // mina, whose colliders are all `blocksLineOfSight: false`).
    // Clustered as in legacy, where it is on unless a scene turns it off and
    // the eras never do; `speed` — see the kaaba note.
    annotations: {
      clustering: { enabled: true, speed: 0.6 },
    },
    models: [
      {
        id: 'kaaba-2000bce-splat',
        file: {
          id: 'kaaba-2000bce-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'kaaba-2000bce',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour the choreography plays
      // by id; no flyover (`routes/kaaba2000BceRoutes.ts`).
      cameraRoutes: KAABA_2000BCE_CAMERA_ROUTES,
      // EXPLICIT identity (world-scale Y-up export) — omit ≠ identity (L7).
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // The eras' ONLY vertical limit is the polar CAP (camera never dips
          // below ~horizon of the target) — and unlike min-polar (CR C7) the
          // SDK exposes `maxPolarAngle` declaratively: a native smooth clamp,
          // no runtime-constraints choreography. 85° here.
          maxPolarAngle: config.controls?.orbitMaxPolarAngle ?? Math.PI,
          disablePan: opts.isTouch,
        },
        home: HOME_AIM_RETURN,
      },
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            // Bronze Age Kaaba walls — legacy OBB, 91° around Y.
            id: 'kaaba-cube',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [-0.9709499216898674, 0.1708300271585894, -0.8054852677447097],
              quaternion: [0, 0.7132504492, 0, 0.7009092643],
              scale: [3.5, 1.7, 2.7],
            },
          },
          {
            // Sole camera cage. No floor raise (legacy clamps were ±Infinity —
            // the raw cylinder governed Y), unlike arafat/muzdalifah.
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            transform: {
              position: [-1.0418837155173448, 0.24, -0.7924066976394661],
              quaternion: [0, 0, 0, 1],
              scale: [18, 7, 18],
            },
          },
        ],
      },
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        // Identity scene → no previewTransform (appearance default IS identity).
        // Legacy-parity reveal (`revealDistanceScale: 0.8`, `revealMode: '3d'`,
        // fixed origin at the era's semantic center):
        reveal: {
          type: 'spread',
          origin: [-1.05, 0.35, -0.81],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 1.25,
          startRadius: 0.625,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildKaaba605Scene: NativeSceneBuilder = (config, opts) => {
  // ~605 C.E Kaaba era — same skeleton as 2000 B.C.E, era-specific numbers.
  // Camera = the cinematic's starting frame; unlike 2000 B.C.E the scene
  // carries a flyover, so the rest pose is its last view
  // (`preloaderRestPose`, `routes/kaaba605Routes.ts`), not `previewTourEnd`.
  // ⚠️ The desktop splat tier is still `v3` while 1M/600K are `v4` (the
  // orientation re-export touched only the mobile tiers) — if the desktop
  // tier ever renders flipped/rotated vs mobile, that's the reason.
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // World scale 1; k = 0.8 (same wide band as 2000 B.C.E). The reveal origin
  // sits exactly ON the containment axis → max in-cage spherical ≈ 7.8
  // (r=7) → 8 with margin.
  const REVEAL_RADIUS = 8;
  const REVEAL_DURATION = 2.3;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // NO disableOcclusion, clustered — see the 2000 B.C.E note.
    annotations: {
      clustering: { enabled: true, speed: 0.6 },
    },
    models: [
      {
        id: 'kaaba-605-splat',
        file: {
          id: 'kaaba-605-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'kaaba-605',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour and the flyover the
      // choreography plays by id.
      cameraRoutes: KAABA_605_CAMERA_ROUTES,
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // Declarative polar cap — 87° for this era (see the 2000 B.C.E note).
          maxPolarAngle: config.controls?.orbitMaxPolarAngle ?? Math.PI,
          disablePan: opts.isTouch,
        },
        home: HOME_AIM_RETURN,
      },
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            // 605 C.E Kaaba walls — legacy OBB, 9° around Y.
            id: 'kaaba-cube',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [0.24406565926671858, 0.18040189167878068, -0.0650466856549024],
              quaternion: [0, 0.0784590957, 0, 0.9969173337],
              scale: [1.7, 1.2, 1],
            },
          },
          {
            // Sole camera cage, centered exactly on the scene's semantic center.
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            transform: {
              position: [-0.08, 0.24, -0.03],
              quaternion: [0, 0, 0, 1],
              scale: [14, 7, 14],
            },
          },
        ],
      },
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        reveal: {
          type: 'spread',
          origin: [-0.08, 0.24, -0.03],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 1.25,
          startRadius: 0.625,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildKaabaOttomanScene: NativeSceneBuilder = (config, opts) => {
  // Late Ottoman Kaaba (1890s) — same skeleton as the other two real eras.
  // Camera = the cinematic's starting frame; the flyover plays, rest = its
  // last view (`preloaderRestPose`) — as 605 C.E. Both rings (tour and
  // flyover) fly outside the cage; createSdkScene lifts collisions for every
  // cinematic (`routes/kaabaOttomanRoutes.ts`).
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // World scale 1; k = 0.8 → the same wide soft band as the sibling eras
  // (bandWidth 1/k, startRadius 0.5/k). The radius is sized off the MODEL, not
  // the cage (the cage is a camera limit, larger than the model): the preview
  // point cloud spans 4.20 × 0.88 × 4.20 and its farthest corner sits 3.13 from
  // the reveal origin → 3.2 covers it. The band is a larger FRACTION of the
  // radius here than on the bigger eras — that is the legacy softness preserved
  // in world units on a physically smaller scene, not a mistuning.
  const REVEAL_RADIUS = 3.2;

  // Legacy nominal 2.5 s × the ~0.77 quartic-front correction ≈ 1.9.
  const REVEAL_DURATION = 1.9;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    // NO disableOcclusion, clustered — see the 2000 B.C.E note.
    annotations: {
      clustering: { enabled: true, speed: 0.6 },
    },
    models: [
      {
        id: 'kaaba-ottoman-splat',
        file: {
          id: 'kaaba-ottoman-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'kaaba-ottoman',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour and the flyover the
      // choreography plays by id.
      cameraRoutes: KAABA_OTTOMAN_CAMERA_ROUTES,
      transform: { quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // π from legacy — NO polar cap: a cap is measured around the orbit
          // target, which a POI fly-to moves to the POI, and
          // `ajyad-fortress-1890`'s pose sits at polar 93.8° around it
          // (.claude/rules/camera-invariants.md). The floor is the cage below.
          maxPolarAngle: config.controls?.orbitMaxPolarAngle ?? Math.PI,
          disablePan: opts.isTouch,
        },
        home: HOME_AIM_RETURN,
      },
      collisions: {
        enabled: true,
        camera: { radius: 0.05 },
        colliders: [
          {
            // Ottoman Kaaba structure — legacy AABB (no rotation).
            id: 'kaaba-cube',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [-0.02682253417921851, -0.2068711253787996, 0.02490459552269486],
              quaternion: [0, 0, 0, 1],
              scale: [0.8, 0.8, 0.6],
            },
          },
          {
            // Adjacent building — legacy OBB, −40° around Y.
            id: 'building-1',
            shape: 'box',
            role: 'obstacle',
            transform: {
              position: [-0.8390320302776392, -0.328573533766111, 1.334348933714039],
              quaternion: [0, -0.3420201433, 0, 0.9396926208],
              scale: [0.8, 0.7, 0.6],
            },
          },
          {
            // Camera cage = legacy `scene-boundary` (r 3.5) with legacy's
            // `clampMinHeight: -0.55` folded in as its floor — the SDK path
            // applies no Y clamp, so the cylinder's Y extent IS the floor and
            // ceiling. The solver keeps the camera radius + skin (0.06) inside,
            // so: camera floor −0.55, ceiling 1.00 (legacy cage top 1.05 − its
            // 0.05 radius), XZ limit 3.45 — legacy's numbers exactly.
            id: 'scene-boundary',
            shape: 'cylinder',
            role: 'containment',
            transform: {
              position: [0.01, 0.225, 0.13],
              quaternion: [0, 0, 0, 1],
              scale: [7.02, 1.67, 7.02],
            },
          },
        ],
      },
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        reveal: {
          type: 'spread',
          origin: [0.01, -0.45, 0.13],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 1.25,
          startRadius: 0.625,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

const buildKaabaFutureScene: NativeSceneBuilder = (config, opts) => {
  // Kaaba of the 2030s — the Ottoman skeleton (no cage, reveal radius sized off
  // the model). Camera = the cinematic's starting frame; the flyover plays,
  // rest = its last view (`preloaderRestPose`). The preview tour rings
  // 6.57 from the target against `orbitMaxDistance` 4.6 — a route applies no
  // orbit limits (`routes/kaabaFutureRoutes.ts`).
  const initial = config.camera.initialPosition;
  const camPos = {
    x: initial.x,
    y: Math.max(initial.y, config.controls?.clampMinHeight ?? initial.y),
    z: initial.z,
  };
  const camTarget = config.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };

  // The v3 export sits +10 up Y and legacy cancels it in `splat.defaultTransform`
  // (position only). Read as DATA so a re-export at the origin drops it in one
  // place. The preview inherits it — `previewTransform` is applied after the
  // scene transform — so there is no previewTransform here.
  const offset = config.splat.defaultTransform?.position ?? { x: 0, y: 0, z: 0 };

  const sky = config.environment.background.skybox;
  const skyUrl = pickSkyboxUrl(sky);

  // World scale 1; k = 0.6 → bandWidth 1/k, startRadius 0.5/k. No cage, so the
  // radius is sized off the MODEL: the preview cloud (after the −10) spans
  // 5.97 × 1.93 × 5.97 and its farthest corner sits 5.04 from the reveal
  // origin → 5.4. The origin is WORLD-space — the engine maps it to
  // splat-local through the splat's inverse world matrix, so the −10 needs no
  // compensation.
  const REVEAL_RADIUS = 5.4;

  // Legacy nominal 2.5 s × the ~0.77 quartic-front correction ≈ 1.9.
  const REVEAL_DURATION = 1.9;

  return defineScene({
    version: '0.3',
    id: config.id,
    createdAt: EPOCH,
    models: [
      {
        id: 'kaaba-future-splat',
        file: {
          id: 'kaaba-future-splat-file',
          fileUrl: pickSplatUrl(config, opts),
          sizeBytes: 0,
          filename: 'kaaba-future',
          uploadedAt: EPOCH,
        },
        isVisible: true,
        createdAt: EPOCH,
      },
    ],
    settings: {
      // Preloader flights as SDK scene data — the tour and the flyover the
      // choreography plays by id.
      cameraRoutes: KAABA_FUTURE_CAMERA_ROUTES,
      transform: { position: [offset.x, offset.y, offset.z], quaternion: [0, 0, 0, 1] },
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        lookAt: [camTarget.x, camTarget.y, camTarget.z],
        fov: config.camera.fov,
      },
      cameraMode: 'orbit',
      cameraController: {
        projection: { near: config.camera.near, far: config.camera.far },
        orbit: {
          minDistance: config.controls?.orbitMinDistance ?? 0,
          maxDistance: config.controls?.orbitMaxDistance ?? 1000,
          // Declarative polar cap — 87° for this era.
          maxPolarAngle: config.controls?.orbitMaxPolarAngle ?? Math.PI,
          disablePan: opts.isTouch,
        },
        home: HOME_AIM_RETURN,
      },
      // NO collisions: legacy has `colliders: []` (not tuned yet) — the orbit is
      // bounded only by maxDistance and the polar cap.
      environment: {
        background: hexToRgba(config.renderer.clearColor),
        skybox: skyUrl
          ? {
              mode: 'equirectangular',
              url: skyUrl,
              url2k: sky?.mobileUrl,
              intensity: sky?.intensity ?? 1,
              blur: sky?.blur ?? 0,
              rotation: sky?.rotation,
            }
          : undefined,
      },
      appearance: {
        transitionMode: 'manual',
        previewModelUrl: config.previewModel.url,
        reveal: {
          type: 'spread',
          origin: [-0.02, -0.54, -0.01],
          radius: REVEAL_RADIUS,
          duration: REVEAL_DURATION,
          distanceMetric: 'spherical',
          bandWidth: 1.667,
          startRadius: 0.833,
          alphaOnly: true,
          frontEasing: 'easeIn',
          easing: 'linear',
        },
        unreveal: { type: 'fade', duration: 2, easing: 'easeOut' },
      },
    },
  });
};

/**
 * Builder of every splat scene, keyed by its config id (the inside alias
 * `kaaba-inside-scene` shares the `kaaba-inside` config, so it lands here too).
 * `createSdkScene` throws on an id without one — `sceneRoutes.test.ts` checks
 * that every registered scene has a builder and a cinematic entry.
 */
export const NATIVE_SDK_SCENES: Record<string, NativeSceneBuilder | undefined> = {
  kaaba: buildKaabaScene,
  arafat: buildArafatScene,
  muzdalifah: buildMuzdalifaScene,
  mina: buildMinaScene,
  'kaaba-2000bce': buildKaaba2000BceScene,
  'kaaba-600ce': buildKaaba605Scene,
  'kaaba-1890s': buildKaabaOttomanScene,
  'kaaba-2030': buildKaabaFutureScene,
  'kaaba-inside': buildKaabaInsideScene,
  overall: buildOverallScene,
};

/**
 * Scenes that play the preloader cinematic — the routes the builder carries as
 * `cameraRoutes` (`routes/<scene>Routes.ts`), played by id by
 * `nativeRouteCinematic.ts`. Opt-in: a listed scene must author
 * `settings.camera` as the flight's STARTING frame (see buildKaabaScene) and
 * carry the `PRELOADER_ROUTE_IDS`; its rest pose is where the routes leave the
 * camera (`preloaderRestPose`). All SDK scenes are enabled; a new scene converts
 * its legacy flights once (`routes/convertLegacyPreloader.ts`), adds its id and
 * a row in `routes/sceneRoutes.test.ts`, and makes its reveal origin explicit
 * (a fixed point, never 'camera').
 */
export const SDK_PRELOADER_CINEMATIC_SCENES: ReadonlySet<string> = new Set([
  'kaaba',
  'kaaba-1890s',
  'kaaba-2030',
  'muzdalifah',
  'arafat',
  'mina',
  'kaaba-2000bce',
  'kaaba-600ce',
  'kaaba-inside',
  'overall',
]);
